import { hasModule, permissionsHaveModule } from '@zercode/auth';

import { NavItemType } from 'types';

/**
 * C3 — rol-tabanlı menü gizleme [STD-API-6 savunma derinliği]: API zaten 403 döner,
 * ama izinli olmayan menüyü GÖSTERMEK kötü UX'tir (öğrencinin Tenant Yönetimi görmesi).
 * `permission` alanı taşıyan öğe, kullanıcının /me izin listesinde yoksa gizlenir;
 * children'ı tamamen boşalan gruplar da gizlenir. İzinler yüklenene kadar (me pending)
 * yalnız izin-şartsız öğeler görünür — yasak öğe FLAŞLAMAZ.
 *
 * PA-7: an item may instead declare `module` ({service}.{module}) — shown when the user holds ANY
 * action on it (the derived "Menu" grant). Preferred for taxonomy-migrated modules so a role
 * granted view/add/… via the matrix lights up the menu without pinning it to one legacy code.
 */
export function filterMenuByPermissions(items: NavItemType[], permissions: readonly string[]): NavItemType[] {
  const allowed = new Set(permissions);

  const visit = (item: NavItemType): NavItemType | null => {
    if (item.permission && !allowed.has(item.permission)) {
      return null;
    }

    // PA-7: module-derived visibility — any action on the module suffices.
    if (item.module && !permissionsHaveModule(permissions, item.module)) {
      return null;
    }

    if (item.children) {
      const children = item.children.map(visit).filter((c): c is NavItemType => c !== null);
      if (children.length === 0 && !item.url) {
        return null; // tüm çocukları gizlenen grup da gizlenir
      }
      return { ...item, children };
    }

    return item;
  };

  return items.map(visit).filter((i): i is NavItemType => i !== null);
}

/**
 * C-8 — license-aware menu hiding [STD-API-6 defense-in-depth]: an item carrying `requiredModule`
 * is hidden when the tenant's plan doesn't include it ('*' = all). Groups emptied by this hide too.
 * `modules === null` (entitlements not loaded yet) shows everything (fail open; backend still 403s).
 */
export function filterMenuByModules(items: NavItemType[], modules: string[] | null): NavItemType[] {
  if (modules === null) return items;

  const visit = (item: NavItemType): NavItemType | null => {
    if (item.requiredModule && !hasModule(modules, item.requiredModule)) {
      return null;
    }
    if (item.children) {
      const children = item.children.map(visit).filter((c): c is NavItemType => c !== null);
      if (children.length === 0 && !item.url) {
        return null;
      }
      return { ...item, children };
    }
    return item;
  };

  return items.map(visit).filter((i): i is NavItemType => i !== null);
}
